/**
 * ApprovedTariffSelector - Select from approved custom rates and system tariffs
 * 
 * Fetches from /api/site_dna/v1/projects/custom-rates which returns a union of:
 * 1. User-created Custom Rates (approved)
 * 2. System-generated Approved Tariffs (native 8760)
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    CircularProgress,
    Alert,
    Chip,
    Tooltip
} from '@mui/material';
import { VerifiedUser, ElectricBolt } from '@mui/icons-material';
import axios from 'axios';
import { useAuth, API_BASE_URL } from '@vortx/shared-ui';

/**
 * ApprovedTariffSelector Component
 * 
 * @param {string} currentTariffId - Currently selected tariff ID (if any)
 * @param {Function} onTariffSelect - Callback when tariff is selected (tariffData)
 */
function ApprovedTariffSelector({
    currentTariffId,
    onTariffSelect,
}) {
    const { currentUser } = useAuth();
    const [approvedRates, setApprovedRates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedRateId, setSelectedRateId] = useState('');

    // Fetch approved rates on mount
    useEffect(() => {
        const fetchApprovedRates = async () => {
            setIsLoading(true);
            setError(null);
            try {
                // Get token
                const headers = { 'Content-Type': 'application/json' };
                if (currentUser) {
                    const token = await currentUser.getIdToken();
                    headers['Authorization'] = `Bearer ${token}`;
                } else {
                    headers['X-Dev-User-Id'] = 'tim@vortx.us';
                }

                // Call the unified endpoint - only fetch electricity tariffs
                const res = await axios.get(`${API_BASE_URL}/api/site_dna/v1/projects/custom-rates?tariff_type=electricity`, { headers });

                // The backend returns { rates: [...], count: N }
                const rates = Array.isArray(res.data) ? res.data : (res.data.rates || res.data.custom_rates || []);

                setApprovedRates(rates);

                // If current tariff matches one in the list, select it
                if (currentTariffId) {
                    const found = rates.find(r => r.id === currentTariffId);
                    if (found) {
                        setSelectedRateId(currentTariffId);
                    }
                }
            } catch (err) {
                console.error('Error fetching approved rates:', err);
                setError('Failed to load approved rates');
            } finally {
                setIsLoading(false);
            }
        };

        fetchApprovedRates();
    }, [currentUser, currentTariffId]);

    const handleRateChange = (e) => {
        const rateId = e.target.value;
        setSelectedRateId(rateId);

        if (rateId) {
            const rate = approvedRates.find(r => r.id === rateId);
            if (rate && onTariffSelect) {
                // Pass the full rate object
                onTariffSelect(rate);
            }
        }
    };

    const hasRates = approvedRates.length > 0;

    if (!hasRates && !isLoading && !error) return null;

    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <VerifiedUser fontSize="small" color="primary" />
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                    Select from Approved Tariffs
                </Typography>
                {isLoading && <CircularProgress size={16} />}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                    {error}
                </Alert>
            )}

            <TextField
                select
                fullWidth
                value={selectedRateId}
                onChange={handleRateChange}
                label="Select an Approved Tariff"
                size="small"
                disabled={isLoading}
                helperText="Includes system-verified rates and approved custom tariffs"
            >
                <MenuItem value="">
                    <em>-- No approved tariff selected --</em>
                </MenuItem>
                {approvedRates.map((rate) => {
                    const isNative = rate.rate_mode === 'native_8760';
                    const isGlobal = rate.is_global;
                    const label = rate.name || rate.label || 'Unnamed Rate';
                    const subLabel = isNative ? 'System Verified (High Precision)' : 'User Custom Rate';

                    return (
                        <MenuItem key={rate.id} value={rate.id}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {rate.utility_name} • {subLabel}
                                    </Typography>
                                </Box>
                                {isNative && (
                                    <Tooltip title="AI Extracted High Fidelity 8760">
                                        <Chip
                                            icon={<ElectricBolt sx={{ fontSize: '14px !important' }} />}
                                            label="AI"
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </MenuItem>
                    );
                })}
            </TextField>
        </Box>
    );
}

export default ApprovedTariffSelector;
